import type { GridSelection } from './useGridSelection';

/**
 * Keyboard navigation for a {@link GridSelection}: arrows/Tab move (Shift
 * extends), Enter/F2 or a digit opens an editor, Delete/Backspace clears, and
 * Escape collapses the selection. Returns a plain `keydown` handler (the host
 * registers it) and delegates editing and clearing to callbacks, so this owns
 * navigation only — no editing state, no persistence.
 *
 * ⌘/Ctrl chords are deliberately ignored here so copy/paste (their own events)
 * take precedence.
 */
export interface GridKeyboardOptions {
  selection: GridSelection;
  /** Guard: true while editing or focus is in a form field (keys pass through). */
  isBusy: () => boolean;
  /** Open the editor on a cell; `seed` pre-fills it (type-to-replace). */
  onEdit: (ri: number, ci: number, seed?: string | null) => void;
  /** Clear the current selection's cells. */
  onClear: () => void;
  /** Extra Escape handling (e.g. dismiss a popover). */
  onEscape?: () => void;
}

export interface GridKeyboard {
  onKeyDown(e: KeyboardEvent): void;
}

export function useGridKeyboard(opts: GridKeyboardOptions): GridKeyboard {
  function onKeyDown(e: KeyboardEvent): void {
    const sel = opts.selection;
    if (opts.isBusy() || !sel.focus.value) return;
    if (e.metaKey || e.ctrlKey) return; // copy/paste handled by their own events
    const f = sel.focus.value;
    const collapse = () => sel.collapseMulti();
    switch (e.key) {
      case 'ArrowUp':
        collapse();
        sel.moveSelection(-1, 0, e.shiftKey);
        e.preventDefault();
        break;
      case 'ArrowDown':
        collapse();
        sel.moveSelection(1, 0, e.shiftKey);
        e.preventDefault();
        break;
      case 'ArrowLeft':
        collapse();
        sel.moveSelection(0, -1, e.shiftKey);
        e.preventDefault();
        break;
      case 'ArrowRight':
        collapse();
        sel.moveSelection(0, 1, e.shiftKey);
        e.preventDefault();
        break;
      case 'Tab':
        collapse();
        sel.moveSelection(0, e.shiftKey ? -1 : 1);
        e.preventDefault();
        break;
      case 'Enter':
      case 'F2':
        opts.onEdit(f.ri, f.ci);
        e.preventDefault();
        break;
      case 'Delete':
      case 'Backspace':
        opts.onClear();
        e.preventDefault();
        break;
      case 'Escape':
        sel.collapseToFocus();
        opts.onEscape?.();
        e.preventDefault();
        break;
      default:
        if (/^\d$/.test(e.key)) {
          opts.onEdit(f.ri, f.ci, e.key);
          e.preventDefault();
        }
    }
  }

  return { onKeyDown };
}
